import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { Mic, Plus, Upload, X, ArrowRight, Check } from 'lucide-react';
import axios from '../../../axios';
import apiError from '../../../error';
import { toast } from 'react-toastify';
import Spinner from '../../../component/spinner/spinner';
import { v4 as uuidv4 } from 'uuid';

const DeepfakeAttachmentModal = ({ show, handleClose, templateId, onAttachmentAdded }) => {
    const [voices, setVoices] = useState([]);
    const [selectedVoiceId, setSelectedVoiceId] = useState('');
    const [mode, setMode] = useState('tts'); // 'tts' or 'sts'
    const [stsMode, setStsMode] = useState(null); // 'upload' or 'record'
    const [text, setText] = useState('');
    const [inputAudioBlob, setInputAudioBlob] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [loadingVoices, setLoadingVoices] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const [audioBlob, setAudioBlob] = useState(null);
    const mediaRecorder = useRef(null);
    const audioChunks = useRef([]);

    useEffect(() => {
        if (show) {
            loadVoices();
        }
    }, [show]);

    const loadVoices = () => {
        setLoadingVoices(true);
        axios.get('/api/deepfake/list-voice')
            .then(res => {
                setVoices(res.data.data.voices || []);
                if (res.data.data.voices?.length > 0 && !selectedVoiceId) {
                    setSelectedVoiceId(res.data.data.voices[0].voiceId);
                }
                setLoadingVoices(false);
            })
            .catch(err => {
                setLoadingVoices(false);
                apiError(err);
            });
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorder.current = new MediaRecorder(stream);
            audioChunks.current = [];

            mediaRecorder.current.ondataavailable = (event) => {
                audioChunks.current.push(event.data);
            };

            mediaRecorder.current.onstop = () => {
                const blob = new Blob(audioChunks.current, { type: 'audio/wav' });
                setInputAudioBlob(blob);
            };

            mediaRecorder.current.start();
            setIsRecording(true);
        } catch (err) {
            console.error("Error accessing microphone:", err);
            apiError(err);
        }
    };

    const stopRecording = () => {
        if (mediaRecorder.current && isRecording) {
            mediaRecorder.current.stop();
            setIsRecording(false);
            mediaRecorder.current.stream.getTracks().forEach(track => track.stop());
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setInputAudioBlob(file);
        }
    };

    const handleGenerate = () => {
        if (!selectedVoiceId) {
            toast.error("Please select a voice");
            return;
        }

        if (mode === 'tts') {
            if (!text.trim()) {
                toast.error("Please enter some text");
                return;
            }

            setGenerating(true);
            setAudioUrl(null);
            setAudioBlob(null);

            axios.post('/api/deepfake/text-to-speech', {
                voiceId: selectedVoiceId,
                text: text
            }, {
                responseType: 'arraybuffer'
            })
                .then(response => {
                    const blob = new Blob([response.data], { type: 'audio/mpeg' });
                    const url = URL.createObjectURL(blob);
                    setAudioUrl(url);
                    setAudioBlob(blob);
                    setGenerating(false);
                    toast.success("Audio generated successfully!");
                })
                .catch(err => {
                    setGenerating(false);
                    apiError(err);
                });
        } else {
            if (!inputAudioBlob) {
                toast.error("Please record or upload audio first");
                return;
            }

            setGenerating(true);
            setAudioUrl(null);
            setAudioBlob(null);

            const formData = new FormData();
            formData.append('voiceId', selectedVoiceId);
            formData.append('audio', inputAudioBlob, inputAudioBlob.name || 'input_audio.wav');

            axios.post('/api/deepfake/speech-to-speech', formData, {
                responseType: 'arraybuffer',
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
                .then(response => {
                    const blob = new Blob([response.data], { type: 'audio/mpeg' });
                    const url = URL.createObjectURL(blob);
                    setAudioUrl(url);
                    setAudioBlob(blob);
                    setGenerating(false);
                    toast.success("Audio transformed successfully!");
                })
                .catch(err => {
                    setGenerating(false);
                    apiError(err);
                });
        }
    };

    const handleAddAttachment = () => {
        if (!audioBlob) return;

        const formData = new FormData();
        const filename = `${uuidv4()}.mp3`;
        formData.append('files', audioBlob, filename);
        formData.append('templateId', templateId);

        axios.post('/api/attachment', formData, {
            headers: {
                "Content-type": "multipart/form-data",
            }
        })
            .then(res => {
                toast.success('Audio added as attachment');
                if (onAttachmentAdded) {
                    onAttachmentAdded(res.data.attachment);
                }
                handleClose();
                reset();
            })
            .catch(error => apiError(error));
    };

    const reset = () => {
        setAudioUrl(null);
        setAudioBlob(null);
        setText('');
        setMode('tts');
        setStsMode(null);
        setInputAudioBlob(null);
        setIsRecording(false);
    };

    return (
        <Modal show={show} onHide={() => { handleClose(); reset(); }} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <h5>Deepfake Attachment</h5>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Conversion Mode</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={mode}
                                    onChange={(e) => {
                                        setMode(e.target.value);
                                        setAudioUrl(null);
                                        setAudioBlob(null);
                                    }}
                                >
                                    <option value="tts">Text to Speech</option>
                                    <option value="sts">Speech to Speech</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Select Voice</Form.Label>
                                {loadingVoices ? (
                                    <div className="d-flex align-items-center">
                                        <Spinner loading={true} /> <span className="ml-2">Loading...</span>
                                    </div>
                                ) : (
                                    <Form.Control
                                        as="select"
                                        value={selectedVoiceId}
                                        onChange={(e) => setSelectedVoiceId(e.target.value)}
                                    >
                                        <option value="">Select a voice</option>
                                        {voices.map(voice => (
                                            <option key={voice.voiceId} value={voice.voiceId}>{voice.name}</option>
                                        ))}
                                    </Form.Control>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    {mode === 'tts' ? (
                        <Form.Group className="mb-3">
                            <Form.Label>Text</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Enter the text you want to convert to speech..."
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                            />
                        </Form.Group>
                    ) : (
                        <div className="sts-container mb-3">
                            <Form.Label>Input Audio</Form.Label>
                            {!stsMode ? (
                                <div className="d-flex gap-2">
                                    <Button variant="outline-primary" className="flex-fill" onClick={() => setStsMode('upload')}>
                                        <Upload size={16} className="mr-2" /> Upload Audio
                                    </Button>
                                    <Button variant="outline-primary" className="flex-fill" onClick={() => setStsMode('record')}>
                                        <Mic size={16} className="mr-2" /> Record Audio
                                    </Button>
                                </div>
                            ) : stsMode === 'record' ? (
                                <div className="border rounded p-3 text-center bg-light">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="small font-weight-bold">Recording Mode</span>
                                        <Button variant="link" size="sm" onClick={() => { setStsMode(null); setInputAudioBlob(null); }} className="text-secondary p-0">Change</Button>
                                    </div>
                                    <Button
                                        variant={isRecording ? "danger" : "primary"}
                                        onClick={isRecording ? stopRecording : startRecording}
                                        className="rounded-circle p-3 mb-2"
                                    >
                                        {isRecording ? <X size={24} /> : <Mic size={24} />}
                                    </Button>
                                    <p className="mb-0 small">
                                        {isRecording ? 'Recording... click to stop' : inputAudioBlob ? 'Audio captured!' : 'Click to start recording'}
                                    </p>
                                    {inputAudioBlob && !isRecording && (
                                        <div className="mt-2">
                                            <audio controls src={URL.createObjectURL(inputAudioBlob)} className="w-100" />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="border rounded p-3 bg-light">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="small font-weight-bold">Upload Mode</span>
                                        <Button variant="link" size="sm" onClick={() => { setStsMode(null); setInputAudioBlob(null); }} className="text-secondary p-0">Change</Button>
                                    </div>
                                    <input
                                        type="file"
                                        accept="audio/*"
                                        onChange={handleFileUpload}
                                        className="form-control"
                                    />
                                    {inputAudioBlob && (
                                        <div className="mt-2">
                                            <audio controls src={URL.createObjectURL(inputAudioBlob)} className="w-100" />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="text-right mt-3">
                        <Button
                            variant="primary"
                            onClick={handleGenerate}
                            disabled={generating || loadingVoices || (mode === 'sts' && !inputAudioBlob)}
                        >
                            {generating ? (
                                <><Spinner loading={true} /> Processing...</>
                            ) : (
                                <>{mode === 'tts' ? <Mic size={16} className="mr-2" /> : <ArrowRight size={16} className="mr-2" />} {mode === 'tts' ? 'Generate Audio' : 'Transform Audio'}</>
                            )}
                        </Button>
                    </div>

                    {audioUrl && (
                        <div className="mt-4 p-3 border rounded bg-light border-success">
                            <h6 className="text-success d-flex align-items-center">
                                <Check size={16} className="mr-2" /> Result Audio
                            </h6>
                            <audio controls src={audioUrl} className="w-100 mt-2">
                                Your browser does not support the audio element.
                            </audio>
                        </div>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => { handleClose(); reset(); }}>
                    Cancel
                </Button>
                <Button
                    variant="success"
                    onClick={handleAddAttachment}
                    disabled={!audioBlob}
                >
                    <Plus size={16} className="mr-2" /> Add Attachment
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DeepfakeAttachmentModal;
